import { addDays, differenceInCalendarDays, format, startOfDay } from 'date-fns'
import { find, isEmpty, isNil, uniq } from 'lodash'
import pluralize from 'pluralize'

// A stored source's `page` is a single page ("3") or the comma-separated list
// agent-gpt accumulates when one document answers from several pages ("2, 7").
export const formatSourcePages = (page) => {
  return page.includes(',') ? `(pp. ${page})` : `(p. ${page})`
}

export const formatSourcesForCsv = (sources) => {
  if (isEmpty(sources)) {
    return ''
  }

  return sources
    .map((source) => {
      const pages = isNil(source.page) ? null : formatSourcePages(source.page)
      return [source.title, pages, source.url].filter(Boolean).join(' ')
    })
    .join('; ')
}

// The page's date range holds the selected days, each as its local midnight, stored
// as formatted strings rather than Date objects because that is what agent-gpt's
// query params expect.
const START_OF_DAY = "yyyy-MM-dd'T00:00:00'xxx"

// Normalized first because the picker's presets end on 23:59:59.999, and on a DST
// change day that instant's offset is not midnight's.
export const formatDay = (date) => format(startOfDay(date), START_OF_DAY)

// agent-gpt excludes end_date, so the range runs to the midnight after its last day
// and a question asked in that day's final second still counts.
export const getEndDateParam = (endDate) => formatDay(addDays(new Date(endDate), 1))

// The picker defaults to the trailing week, so the delta names last week as its
// baseline on load and drops the claim as soon as someone selects a different span.
export const getComparisonLabel = ({ startDate, endDate }) => {
  const spanInDays = differenceInCalendarDays(new Date(endDate), new Date(startDate)) + 1

  return spanInDays === 7 ? 'vs. last week' : 'vs. previous period'
}

// Mirrors agent-gpt's gate on verified answers and documents: Balto Cloud access plus
// AgentGPT Editor. Balto admins need the grant too.
export const canEditAgentGpt = ({ balto_cloud_access, verified_answer_editor }) =>
  !!balto_cloud_access && !!verified_answer_editor

// agent-gpt has no stored "answered" flag; it derives one from the reply text, so the
// card explains the rule instead of letting the figure read as a hard count.
export const ANSWERED_TOOLTIP =
  'Every question AgentGPT replied to with a real answer. Replies of "I don\'t have that information yet" or "I couldn\'t verify an exact supported answer" count as unanswered.'

// agent-gpt's per-answer limits, of the same names.
export const VERIFIED_ANSWER_MAX_QUESTION_CHARS = 1000
export const VERIFIED_ANSWER_MAX_ANSWER_CHARS = 5000
export const ANSWER_SAVE_FAILED = "The answer couldn't be saved. Try again."

// Checked before submit so the author learns about a cap before agent-gpt's 409 does it.
// An edit adds no answer, and agent-gpt only refuses one that grows past the budget.
export const getCapReason = (
  { count, limit, chars, char_limit: charLimit },
  size,
  editedAnswer
) => {
  if (isNil(editedAnswer) && count >= limit) {
    return `This organization has reached its limit of ${limit.toLocaleString()} verified answers.`
  }

  const previousSize = isNil(editedAnswer)
    ? 0
    : editedAnswer.question.length + editedAnswer.answer.length
  const charsLeft = charLimit - chars + previousSize

  if (size > previousSize && size > charsLeft) {
    return `Only ${charsLeft.toLocaleString()} of this organization's ${charLimit.toLocaleString()} verified answer characters are left. Shorten the question or answer.`
  }

  return null
}

export const ALL_PLAYBOOKS = 'All playbooks'
export const DELETED_PLAYBOOK = 'Deleted playbook'

// The cids with no playbook option: playbooks deleted since the answer was scoped. Unknowable
// while the options load or after they fail, when every cid would look deleted.
export const getDeletedPlaybookCids = (configCids, { options, loading, failed }) => {
  if (loading || failed) {
    return []
  }

  return configCids.filter((cid) => !options.some((option) => option.value === cid))
}

// Every playbook the answer was limited to is gone, so AgentGPT serves it to no one.
export const isScopeUnused = (configCids, playbooks) =>
  !isEmpty(configCids) && getDeletedPlaybookCids(configCids, playbooks).length === configCids.length

// An answer's playbooks by name. Once the options have loaded, a cid without one is a playbook
// deleted since, and says so as the edit field does; until then only a count is accurate.
export const formatPlaybookScope = (configCids, { options, loading, failed }) => {
  if (loading || failed) {
    return pluralize('playbook', configCids.length, true)
  }

  return uniq(
    configCids.map(
      (cid) => options.find((option) => option.value === cid)?.label ?? DELETED_PLAYBOOK
    )
  ).join(', ')
}

// agent-gpt's citation label, "[Source 2]" or "[Source 2 - Balto product documentation]". The
// spaces before a label go with it; line breaks stay, so markdown lists survive.
const SOURCE_LABEL = /[ \t]*\[Source\s+\d+[^\]]*\]/gi

export const stripSourceLabels = (text) => text.replace(SOURCE_LABEL, '').trim()

// The verified answer a search log ask edits: the one linked to it as a gap, else the one
// AgentGPT answered it from, when it cited exactly one. Null means a new answer.
export const findVerifiedAnswerForAsk = (row, items) => {
  const linked = items.find((item) => item.source_agent_question_ids.includes(row.id))

  if (!isNil(linked)) {
    return linked
  }

  // Only an answered ask can be corrected here (an older agent-gpt omits no_answer); a refusal is
  // answered from Knowledge gaps even when it cited something. Old and desktop-path rows carry
  // null sources.
  if (row.no_answer !== false || isEmpty(row.sources)) {
    return null
  }

  const citedIds = row.sources.map((source) => source.verified_answer_id).filter((id) => !isNil(id))
  const cited = items.filter((item) => citedIds.includes(item.id))

  return cited.length === 1 ? cited[0] : null
}

// Apostrophes and emoji variation selectors (Mn, so \p{M} would keep them) are deleted, not
// spaced: "what's" is "whats". Each word is made singular, so "options" matches "option".
export const normalizeQuestion = (text) =>
  text
    .normalize('NFKC')
    .toLowerCase()
    .replace(/['\u2018\u2019\u02BC]|[\uFE0E\uFE0F]/g, '')
    .replace(/[^\p{L}\p{M}\p{N}]+/gu, ' ')
    .trim()
    .split(' ')
    .map((word) => pluralize.singular(word))
    .join(' ')

// Every verified answer to the same question, in list order: agent-gpt sends the most recently
// updated first. A question with no words matches none.
export const findMatchingVerifiedAnswers = (question, items) => {
  const normalized = normalizeQuestion(question)

  if (isEmpty(normalized)) {
    return []
  }

  return items.filter((item) => normalizeQuestion(item.question) === normalized)
}

// The org the Connect card manages: the signed-in org's own row when it is the
// target, else the row from org 1's organizations list (staff using the filter).
export const findTargetOrganization = ({
  organizationId,
  currentUserOrgId,
  currentOrganization,
  organizations,
}) =>
  organizationId === currentUserOrgId
    ? currentOrganization
    : find(organizations, { id: organizationId })
